/**
 * member-favorites-tab.js — 會員「我的收藏」雙 tab 切換 + 取消收藏
 *
 * 對照 HTML（src/pages/member/favorites.html）：
 *   <div class="tabs tabs--underline" role="tablist" data-member-fav-tabs>
 *     <button class="tabs__item is-active" role="tab" aria-selected="true"
 *             aria-controls="fav-panel-clinics">診所</button>
 *     <button class="tabs__item" role="tab" aria-selected="false"
 *             aria-controls="fav-panel-articles" tabindex="-1">文章</button>
 *   </div>
 *   <div class="member-fav-panel" id="fav-panel-clinics" role="tabpanel">
 *     <ul class="member-fav-list" data-fav-list="clinics"> … <li><article data-fav-item>…
 *       …<button data-fav-remove aria-label="取消收藏 …">取消收藏</button>
 *     <div class="empty-state" data-fav-empty hidden>…</div>
 *   </div>
 *
 * 功能（MEMBER-SPEC §B.1 / §C3 + undo 規格）：
 *   - tab 切換：is-active + aria-selected 同步，panel 顯隱（hidden 屬性），
 *     roving tabindex（選中 tab tabindex 移除、其餘 -1），← → Home End 鍵盤導覽。
 *   - 取消收藏：移除 → pending undo →（復原 | 定案）狀態機：
 *       點「取消收藏」→ li 加 .is-removing（CSS grid-rows collapse + 淡出 + scale），
 *       li 不立即 remove、empty-state 不顯（pending 期間卡邏輯上仍在、可復原）；
 *       浮出 undo snackbar（AdminToast undo 變體：訊息 +「復原」+ 8s 倒數條）。
 *       點「復原」→ 移除 .is-removing（CSS 反向長回原位）+ 焦點回該卡取消鈕。
 *       逾時 8s / 點 × / ESC → 定案：li.remove() + 該 panel 空則顯 empty-state。
 *   - 多筆：同時只一個 snackbar；連續取消第二張 → 前一筆立即定案 + 換新 snackbar（復原只還最近一筆）。
 *   - a11y：snackbar role=status 一次宣告（不另開 live region，避免雙重宣告）；倒數純視覺 aria-hidden；
 *     鍵盤觸發取消時焦點移到「復原」鈕；ESC = 定案。
 *   - reduced-motion 降級由 CSS（member.css / admin.css）負責；倒數與復原功能照常（不受 PRM 影響）。
 *
 * 載入：由 /src/admin.js 中央 import 鏈 auto-init（會員頁殼亦走 admin.js）；undo snackbar 用 admin-toast.js。
 *   無對應 DOM（[data-member-fav-tabs] 不存在）即 no-op，可安全在所有會員頁載入。
 */

import { AdminToast } from './admin-toast.js';

// 同時只允許一個待定 undo 作業（§4 單 snackbar）。新取消進來先 flush 前一筆。
let pendingUndo = null;

// undo 自動定案視窗（用戶回饋「復原太快消失」→ 5000 拉長到 8000，2026-06-04）。
// 此值同時驅動 timer（AdminToast duration）與倒數條（toast 以此設 --admin-toast-duration，
// CSS scaleX 動畫時長＝此值），故改這一處即同步「計時 + 倒數條視覺收縮」。
const UNDO_DURATION = 8000;

function initTabs(tablist) {
  const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
  if (!tabs.length) return;

  const panelFor = (tab) => document.getElementById(tab.getAttribute('aria-controls'));

  function activate(tab, { focus = false } = {}) {
    tabs.forEach((t) => {
      const selected = t === tab;
      t.classList.toggle('is-active', selected);
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.tabIndex = selected ? 0 : -1;
      const panel = panelFor(t);
      if (panel) {
        if (selected) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
      }
    });
    if (focus) tab.focus();
  }

  tablist.addEventListener('click', (e) => {
    const tab = e.target.closest('[role="tab"]');
    if (tab && tabs.includes(tab)) activate(tab);
  });

  tablist.addEventListener('keydown', (e) => {
    const current = e.target.closest('[role="tab"]');
    if (!current || !tabs.includes(current)) return;
    const idx = tabs.indexOf(current);
    let next;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = tabs[(idx + 1) % tabs.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = tabs[(idx - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }
    e.preventDefault();
    activate(next, { focus: true });
  });
}

// 取卡片顯示名稱：優先卡標題全名（.fav-card__name / .fav-card__title 的連結文字），
// 退回取消鈕 aria-label 去前綴「取消收藏 」。對齊 §7 copy。
function favName(item, btn) {
  const titleLink = item.querySelector('.fav-card__name a, .fav-card__title a');
  const fromTitle = titleLink && titleLink.textContent.trim();
  if (fromTitle) return fromTitle;
  const label = btn.getAttribute('aria-label') || '';
  return label.replace(/^取消收藏\s*/, '').trim() || '此項目';
}

// 定案：把該 panel 清空後顯 empty-state（[hidden] 守衛見 member.css）。
function finalizeRemoval(li, list, panel) {
  li.remove();
  if (list && !list.querySelector('[data-fav-item]')) {
    list.setAttribute('hidden', '');
    const empty = panel && panel.querySelector('[data-fav-empty]');
    if (empty) empty.removeAttribute('hidden');
  }
}

function initRemoval(root) {
  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-fav-remove]');
    if (!btn) return;

    const item = btn.closest('[data-fav-item]');
    if (!item) return;
    const li = item.closest('li') || item;
    const list = li.closest('[data-fav-list]');
    const panel = li.closest('.member-fav-panel');

    // §4：連續取消 → 前一筆立即定案（idempotent），舊 snackbar 不等退場直接被新筆取代。
    if (pendingUndo) pendingUndo.flush();

    const name = favName(item, btn);
    // 鍵盤觸發（Enter/Space）click detail === 0；滑鼠點擊 detail ≥ 1。僅鍵盤才搶焦點到「復原」鈕。
    const keyboardTriggered = e.detail === 0;

    // 進入 pending：加 .is-removing 觸發 CSS collapse（li 不 remove、empty-state 不顯，§3）。
    item.classList.add('is-removing');

    // 本筆的 undo 句柄；done 旗標確保 finalize / restore 互斥且只跑一次。
    const undo = { done: false };

    const finalize = () => {
      if (undo.done) return;
      undo.done = true;
      if (pendingUndo === undo) pendingUndo = null;
      finalizeRemoval(li, list, panel);
    };

    const restore = () => {
      if (undo.done) return;
      undo.done = true;
      if (pendingUndo === undo) pendingUndo = null;
      // 反向：移除 .is-removing → CSS 長回原位（DOM 順序未動，天然回原位）。
      item.classList.remove('is-removing');
      // 焦點回該卡取消鈕（動作起點）；卡已長回、鈕仍在 DOM。
      if (btn.isConnected) btn.focus();
    };

    // flush：被下一筆取消擠掉時，立即定案並收掉自己的 snackbar（finalize 已跑，dismiss skipFinalize 免重跑）。
    undo.flush = () => {
      if (undo.done) return;
      finalize();
      if (undo.toast) undo.toast.dismiss({ skipFinalize: true });
    };

    pendingUndo = undo;

    // 浮出 undo snackbar（role=status 即宣告，不另開 live region → 避免雙重宣告，§6）。
    undo.toast = AdminToast.show({
      type: 'info',
      title: `已取消收藏「${name}」`,
      duration: UNDO_DURATION,
      countdown: true,
      action: { label: '復原', onClick: restore },
      onTimeout: finalize, // 逾時 / 點 × / ESC → 定案
    });

    // 鍵盤觸發：焦點移到「復原」鈕（避免取消鈕隨卡收合後焦點丟失）。滑鼠點擊不搶焦點。
    if (keyboardTriggered) undo.toast.focusAction();
  });
}

function init(root = document) {
  const tablist = root.querySelector('[data-member-fav-tabs]');
  if (!tablist) return; // no-op：非 favorites 頁
  initTabs(tablist);
  // 取消收藏綁在 main 容器（兩 panel 共用一個委派）
  const scope = root.querySelector('.admin-main__inner') || document;
  initRemoval(scope);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}
